import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./CustomSelect.module.scss";

export type SelectOption = { value: string; label: string };

interface Props {
  id?: string;
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const CustomSelect: React.FC<Props> = ({
  id,
  label,
  placeholder = "Выберите",
  options,
  value,
  onChange,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);

  // Поиск по подстроке, без учёта регистра, обновляется на каждый символ.
  // Совпадения с начала названия показываем первыми: набирая «мер», водитель
  // ждёт «Мерседес» вверху, а не марку, где эти буквы где-то в середине.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    const matches = options.filter((o) => o.label.toLowerCase().includes(q));
    return matches.sort((a, b) => {
      const aStarts = a.label.toLowerCase().startsWith(q) ? 0 : 1;
      const bStarts = b.label.toLowerCase().startsWith(q) ? 0 : 1;
      return aStarts - bStarts;
    });
  }, [options, query]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // При открытии — фокус в поле поиска и подсветка выбранного варианта.
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const idx = options.findIndex((o) => o.value === value);
    setActiveIdx(idx >= 0 ? idx : 0);
  }, [open, value, options]);

  // При наборе список сужается — держим подсветку на первом совпадении.
  useEffect(() => {
    if (open && query) setActiveIdx(0);
  }, [open, query]);

  useEffect(() => {
    if (!open || activeIdx < 0 || !listRef.current) return;
    const el = listRef.current.children[activeIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [open, activeIdx]);

  const commit = (v: string) => {
    onChange(v);
    close();
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!open) {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    // Открыто: пробел набирается в поиске, поэтому выбираем только по Enter.
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIdx(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIdx(filtered.length - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && activeIdx < filtered.length) {
        commit(filtered[activeIdx].value);
      }
    } else if (e.key === "Tab") {
      close();
    }
  };

  const listId = id ? `${id}-listbox` : undefined;

  return (
    <div className={styles.root} ref={rootRef}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
      )}

      {open ? (
        <div className={`${styles.trigger} ${styles.open}`}>
          <input
            ref={inputRef}
            id={id}
            type="text"
            className={styles.search}
            value={query}
            placeholder="Найти"
            autoComplete="off"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKey}
          />
          <span className={styles.chevron} aria-hidden="true" />
        </div>
      ) : (
        <button
          type="button"
          id={id}
          className={`${styles.trigger}${
            !selected ? " " + styles.isPlaceholder : ""
          }`}
          aria-haspopup="listbox"
          aria-expanded={false}
          disabled={disabled}
          onClick={() => !disabled && setOpen(true)}
          onKeyDown={handleKey}
        >
          <span className={styles.value}>{selected?.label ?? placeholder}</span>
          <span className={styles.chevron} aria-hidden="true" />
        </button>
      )}

      {open && (
        <ul ref={listRef} id={listId} role="listbox" className={styles.list}>
          {filtered.length === 0 ? (
            <li className={styles.empty}>
              {options.length === 0 ? "Нет вариантов" : "Ничего не найдено"}
            </li>
          ) : (
            filtered.map((opt, i) => (
              <li
                key={opt.value}
                role="option"
                aria-selected={value === opt.value}
                className={`${styles.option}${
                  i === activeIdx ? " " + styles.active : ""
                }${value === opt.value ? " " + styles.selected : ""}`}
                onMouseEnter={() => setActiveIdx(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(opt.value)}
              >
                <span className={styles.optionLabel}>{opt.label}</span>
                {value === opt.value && (
                  <span className={styles.check} aria-hidden="true" />
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default CustomSelect;
